/**
 * Estado de sincronização (Zustand).
 *
 * O iOS não tem Background Sync, então drenar o outbox é sempre uma ação
 * explícita. Três gatilhos, todos aprovados:
 *   1. abrir ou retomar o app;
 *   2. detectar rede enquanto o app está ativo;
 *   3. o botão "Sincronizar agora".
 *
 * Nada é descartado em silêncio: `pending`, `conflicts` e `lastError` ficam
 * visíveis, e conflito só sai da fila por decisão do usuário.
 */

import {
  conflictCount,
  drainOutbox,
  type Mutation,
  type MutationSender,
  type OutboxStore,
  pendingCount,
  resolveConflict,
} from '@nv/offline';
import { MENSAGEM_SYNC_FAILURE } from '@nv/core';

import { jaCapturado, reporter } from '../observability/instalar';
import { create } from 'zustand';

export type SyncStatus = 'idle' | 'syncing' | 'done' | 'error' | 'offline';

interface SyncState {
  status: SyncStatus;
  pending: number;
  conflicts: number;
  /** Mutações em conflito, para a folha de revisão. */
  conflicted: Mutation[];
  lastSyncedAt: number | null;
  lastError: string | null;
  refreshCounts: (store: OutboxStore) => Promise<void>;
  syncNow: (store: OutboxStore, send: MutationSender) => Promise<void>;
  /** Decisão do usuário. Nunca automática. */
  resolve: (
    store: OutboxStore,
    mutationId: string,
    resolution: 'keep_server' | 'apply_mine',
  ) => Promise<void>;
}

/**
 * Pedido de sync que chegou COM um ciclo em curso (#88). Enfileirar uma
 * mutação enquanto o drain anterior ainda roda dispara `syncNow` de novo —
 * e ele era descartado, deixando a mutação nova "pendente" até o próximo
 * gatilho externo (voltar a rede, reabrir o app, "Sincronizar agora"). Agora
 * o pedido fica anotado e o ciclo corrente, ao terminar, drena outra vez.
 * Um único ciclo por vez continua valendo: nada é enviado em paralelo.
 */
let pedidoDuranteSync = false;

export const useSyncStore = create<SyncState>((set, get) => ({
  status: 'idle',
  pending: 0,
  conflicts: 0,
  conflicted: [],
  lastSyncedAt: null,
  lastError: null,

  refreshCounts: async (store) => {
    const fila = await store.list();
    set({
      pending: pendingCount(fila),
      conflicts: conflictCount(fila),
      conflicted: fila.filter((m) => m.status === 'conflict'),
    });
  },

  resolve: async (store, mutationId, resolution) => {
    await resolveConflict(store, mutationId, resolution);
    await get().refreshCounts(store);
  },

  syncNow: async (store, send) => {
    if (get().status === 'syncing') {
      pedidoDuranteSync = true;
      return;
    }

    // Repete enquanto alguém tiver pedido durante o ciclo. Sem rede ou com o
    // drain interrompido, para: o `online` e a abertura do app são os gatilhos.
    do {
      pedidoDuranteSync = false;
      const continua = await umCiclo(store, send);
      if (!continua) return;
    } while (pedidoDuranteSync);
  },
}));

/** Um ciclo de drain. Devolve `true` se terminou normalmente (pode repetir). */
async function umCiclo(store: OutboxStore, send: MutationSender): Promise<boolean> {
  const { setState: set, getState: get } = useSyncStore;
  {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      set({ status: 'offline' });
      await get().refreshCounts(store);
      return false;
    }

    set({ status: 'syncing', lastError: null });

    try {
      const resultado = await drainOutbox(store, send);
      await get().refreshCounts(store);

      if (resultado.stoppedEarly) {
        set({ status: 'offline' });
        return false;
      }

      set({ status: 'done', lastSyncedAt: Date.now() });
      return true;
    } catch (error) {
      // Se a própria infra local caiu (IndexedDB), recontar também falha —
      // e a exceção do catch escaparia do syncNow. Recontagem é melhor esforço.
      await get().refreshCounts(store).catch(() => undefined);
      set({
        status: 'error',
        lastError: error instanceof Error ? error.message : 'Falha ao sincronizar.',
      });
      // Diagnóstico (rodada 2B1): só a exceção INESPERADA chega aqui — offline,
      // retry, 409 e rejeição de domínio são resultados do `drainOutbox`, não
      // exceções. Nada da mutação vai junto: mensagem fixa, nome e frames.
      // Um erro que a camada de rede já contabilizou não conta de novo.
      try {
        if (!jaCapturado(error)) reporter().capturar({ source: 'sync_failure', error, message: MENSAGEM_SYNC_FAILURE });
      } catch {
        /* o reporter nunca derruba a sincronização */
      }
      return false;
    }
  }
}
